import { queryOptions } from '@tanstack/react-query'

import type { DashboardUser } from '../auth/auth-adapter'
import type { OnboardingApiClient } from './onboarding-api'

export function onboardingStatusQueryKey(uid: string) {
  return ['onboarding', 'status', uid] as const
}

export function onboardingProfileQueryKey(uid: string) {
  return ['onboarding', 'profile', uid] as const
}

export function onboardingNexHealthQueryKey(uid: string) {
  return ['onboarding', 'nexhealth', uid] as const
}

export function onboardingSchedulingQueryKey(uid: string) {
  return ['onboarding', 'scheduling', uid] as const
}

export function onboardingCommunicationsQueryKey(uid: string) {
  return ['onboarding', 'communications', uid] as const
}

export function onboardingReviewQueryKey(uid: string) {
  return ['onboarding', 'review', uid] as const
}

export function onboardingValidationQueryKey(uid: string) {
  return ['onboarding', 'validation', uid] as const
}

export function onboardingProvisioningProgressQueryKey(uid: string) {
  return ['onboarding', 'provisioning-progress', uid] as const
}

export function onboardingProvisioningProgressQueryOptions(
  client: OnboardingApiClient,
  user: DashboardUser
) {
  return queryOptions({
    queryKey: onboardingProvisioningProgressQueryKey(user.uid),
    queryFn: () => {
      if (!client.getProvisioningProgress) {
        throw new Error('Provisioning progress API is unavailable')
      }
      return client.getProvisioningProgress(user)
    },
    staleTime: 0,
    refetchInterval: 5_000,
  })
}

export function onboardingReviewQueryOptions(
  client: OnboardingApiClient,
  user: DashboardUser
) {
  return queryOptions({
    queryKey: onboardingReviewQueryKey(user.uid),
    queryFn: () => client.getReview(user),
    staleTime: 0,
  })
}

export function onboardingCommunicationsQueryOptions(
  client: OnboardingApiClient,
  user: DashboardUser
) {
  return queryOptions({
    queryKey: onboardingCommunicationsQueryKey(user.uid),
    queryFn: () => client.getCommunications(user),
    staleTime: 0,
  })
}

export function onboardingSchedulingQueryOptions(
  client: OnboardingApiClient,
  user: DashboardUser
) {
  return queryOptions({
    queryKey: onboardingSchedulingQueryKey(user.uid),
    queryFn: () => client.getScheduling(user),
    staleTime: 0,
  })
}

export function onboardingNexHealthQueryOptions(
  client: OnboardingApiClient,
  user: DashboardUser
) {
  return queryOptions({
    queryKey: onboardingNexHealthQueryKey(user.uid),
    queryFn: () => client.getNexHealth(user),
    staleTime: 0,
  })
}

export function onboardingProfileQueryOptions(
  client: OnboardingApiClient,
  user: DashboardUser
) {
  return queryOptions({
    queryKey: onboardingProfileQueryKey(user.uid),
    queryFn: () => client.getProfile(user),
    staleTime: 0,
  })
}

export function onboardingStatusQueryOptions(
  client: OnboardingApiClient,
  user: DashboardUser
) {
  return queryOptions({
    queryKey: onboardingStatusQueryKey(user.uid),
    queryFn: () => client.getStatus(user),
    staleTime: 0,
    refetchInterval: (query) => {
      const data = query.state.data
      return data && 'status' in data && data.status === 'PROVISIONING'
        ? 5_000
        : false
    },
  })
}
